import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CustomPagination from '../../../components/customPagination';
import { formatMinorToMajorAmount } from '../../../utils/currency';
import { useGetProductsHandler } from '../dataHandlers/getProductsHandler';
import ProductActionMenu from './ProductActionMenu';

const PRODUCT_TYPE_LABELS = {
  prepaid: 'Prepaid',
  postpaid: 'Postpaid',
};

const PRICING_MODEL_LABELS = {
  licensed: 'Flat Rate',
  metered: 'Metered',
};

const formatBillingInterval = (value, unit) => {
  if (value === null || value === undefined || value === '' || !unit) {
    return 'NA';
  }

  const unitLabel = Number(value) === 1 ? unit : `${unit}s`;

  return `${value} ${unitLabel}`;
};

const formatPricingModel = (pricingModel) => PRICING_MODEL_LABELS[pricingModel] || pricingModel || '—';

const formatPriceAmount = (unitAmount) => {
  if (unitAmount === null || unitAmount === undefined || unitAmount === '') {
    return 'Usage based';
  }

  return formatMinorToMajorAmount(unitAmount, { emptyValue: 'Usage based' });
};

const formatPriceLimit = (limit) => {
  if (limit === null || limit === undefined || limit === '' || Number(limit) === 0) {
    return 'Unlimited';
  }

  return String(limit);
};

const renderCenteredCell = (content) => (
  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
    {typeof content === 'string' || typeof content === 'number' ? (
      <Typography
        variant="body2"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {content}
      </Typography>
    ) : (
      content
    )}
  </Box>
);

const renderProductPricesCell = (prices) => {
  if (!Array.isArray(prices) || prices.length === 0) {
    return renderCenteredCell(
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>,
    );
  }

  return renderCenteredCell(
    <Stack spacing={0.5} sx={{ width: '100%' }}>
      {prices.map((price, index) => {
        const name = price.name || '—';
        const details = `${formatPricingModel(price.pricing_model)} - ${formatPriceAmount(price.unit_amount)} - ${formatPriceLimit(price.limit)}`;

        return (
          <Typography
            key={price.stripe_price_id || `${price.name}-${index}`}
            variant="body2"
            title={`${name} (${details})`}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              ({details})
            </Typography>
          </Typography>
        );
      })}
    </Stack>,
  );
};

export default function ProductsTable({
  handlePaginationChange,
  searchQuery = {},
  paginationModel = { page: 0, pageSize: 10 },
}) {
  const { products, totalDocuments, isLoading } = useGetProductsHandler({ searchQuery });

  const handleCopyId = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      // Ignore clipboard errors to avoid blocking table interactions.
    }
  };

  const renderCopyableIdCell = (value, to) =>
    renderCenteredCell(
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
        {to ? (
          <Link
            component={RouterLink}
            to={to}
            underline="hover"
            variant="body2"
            title={value}
            sx={{
              minWidth: 0,
              flexGrow: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </Link>
        ) : (
          <Typography
            variant="body2"
            title={value || '-'}
            sx={{
              minWidth: 0,
              flexGrow: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value || '-'}
          </Typography>
        )}
        <Tooltip title="Copy ID">
          <span>
            <IconButton
              size="small"
              color="primary"
              sx={{ ml: 0.5, flexShrink: 0 }}
              aria-label={`copy-${value || 'id'}`}
              disabled={!value}
              onClick={(event) => {
                event.stopPropagation();
                handleCopyId(value);
              }}
            >
              <ContentCopy fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>,
    );

  const columns = [
    {
      field: 'stripe_product_id',
      headerName: 'Stripe Product ID',
      flex: 0.5,
      renderCell: (params) => renderCopyableIdCell(params.value, `/products/${params.value}`),
    },
    { field: 'name', headerName: 'Name', flex: 1, renderCell: (params) => renderCenteredCell(params.value || '—') },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.25,
      renderCell: (params) => renderCenteredCell(PRODUCT_TYPE_LABELS[params.value] || params.value || 'NA'),
    },
    {
      field: 'billing_interval',
      headerName: 'Billing Interval',
      flex: 0.5,
      renderCell: (params) =>
        renderCenteredCell(
          formatBillingInterval(params.row?.billing_interval_value, params.row?.billing_interval_unit),
        ),
    },
    {
      field: 'prices',
      headerName: 'Prices',
      flex: 1.5,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => renderProductPricesCell(params.row?.prices),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.25,
      renderCell: (params) => renderCenteredCell(<ProductActionMenu product={params.row} />),
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={products}
        columns={columns}
        getRowId={(row) => row.stripe_product_id}
        getRowHeight={() => 'auto'}
        paginationMode="server"
        rowCount={totalDocuments}
        loading={isLoading}
        paginationModel={paginationModel}
        pageSizeOptions={[10, 25]}
        disableColumnMenu
        disableColumnSorting
        disableColumnFilter
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => handlePaginationChange(model.page, model.pageSize)}
        slots={{ pagination: CustomPagination }}
      />
    </Box>
  );
}
